import { useState, useEffect } from "react";
import { api, type CourseEdition, type DiplomadoEdition } from "@/lib/api";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  editionId: string;
  editionLabel: string;
  courseName: string;
  coursePrice: number;
  amountPaid: number;
  onSuccess: () => void;
  serviceType?: "course" | "diplomado";
}

export default function InscripcionModal({
  isOpen, onClose, editionId, editionLabel, courseName, coursePrice, amountPaid, onSuccess, serviceType = "course",
}: Props) {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", company: "", position: "", id_number: "",
    emergency_contact: "", emergency_phone: "",
    payment_option: "none" as "none" | "partial" | "full",
    payment_amount: "", payment_method: "transferencia", payment_reference: "",
  });
  const [proof, setProof] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (!form.full_name || !form.email || !form.phone) {
      setError("Complete los campos obligatorios.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      fd.append(serviceType === "diplomado" ? "diplomado_edition_id" : "edition_id", editionId);
      if (proof) fd.append("proof", proof);
      await api.publicEnrollment(fd);
      setSuccess(true);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al inscribirse");
    }
    setLoading(false);
  };

  const handleClose = () => {
    setSuccess(false);
    setError("");
    setForm({
      full_name: "", email: "", phone: "", company: "", position: "", id_number: "",
      emergency_contact: "", emergency_phone: "",
      payment_option: "none", payment_amount: "", payment_method: "transferencia", payment_reference: "",
    });
    setProof(null);
    onClose();
  };

  if (!isOpen) return null;

  const fieldLabels: Record<string, string> = {
    full_name: "Nombre completo *",
    email: "Correo electrónico *",
    phone: "Teléfono *",
    company: "Empresa",
    position: "Cargo",
    id_number: "Cédula / ID",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose}></div>
      <div className="relative bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6 border-b border-[var(--color-border)] flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold">Inscripción</h2>
            <p className="text-xs text-[var(--color-text-muted)]">{courseName} — {editionLabel}</p>
          </div>
          <button onClick={handleClose} className="cursor-pointer"><i className="ri-close-line text-xl"></i></button>
        </div>

        {success ? (
          <div className="p-8 text-center">
            <i className="ri-check-line text-4xl text-green-400"></i>
            <p className="mt-4 font-semibold">Inscripción enviada</p>
            <p className="text-sm text-[var(--color-text-muted)] mt-2">Revise su correo para confirmación y acceso al portal.</p>
            <p className="text-xs text-[var(--color-text-muted)] mt-3">Guarde su referencia de pago que aparecerá en el correo de confirmación.</p>
            <button onClick={handleClose} className="btn-primary mt-6 cursor-pointer">Cerrar</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-3">
            <p className="text-sm text-[var(--color-accent-oil)] font-semibold">${coursePrice.toLocaleString("en-US")} USD</p>
            {(["full_name", "email", "phone", "company", "position", "id_number"] as const).map((field) => (
              <input key={field} name={field} className="input-field" placeholder={fieldLabels[field]}
                value={form[field]}
                onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                required={["full_name", "email", "phone"].includes(field)} />
            ))}
            <div className="grid grid-cols-2 gap-2">
              <input name="emergency_contact" className="input-field" placeholder="Contacto emergencia" value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} />
              <input name="emergency_phone" className="input-field" placeholder="Tel. emergencia" value={form.emergency_phone} onChange={(e) => setForm({ ...form, emergency_phone: e.target.value })} />
            </div>
            <select name="payment_option" className="input-field" value={form.payment_option}
              onChange={(e) => setForm({ ...form, payment_option: e.target.value as typeof form.payment_option })}>
              <option value="none">Sin pago (inscripción pendiente)</option>
              <option value="partial">Abono parcial</option>
              <option value="full">Pago completo</option>
            </select>
            {form.payment_option !== "none" && (
              <>
                <input name="payment_amount" type="number" className="input-field" placeholder="Monto"
                  value={form.payment_amount} onChange={(e) => setForm({ ...form, payment_amount: e.target.value })} required />
                <input name="payment_reference" className="input-field" placeholder="Referencia (opcional)"
                  value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} />
                <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={(e) => setProof(e.target.files?.[0] || null)} className="input-field" />
              </>
            )}
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button type="submit" disabled={loading} className="btn-primary w-full cursor-pointer">{loading ? "Enviando..." : "Enviar inscripción"}</button>
          </form>
        )}
      </div>
    </div>
  );
}

export function InscripcionFromEdition({ edition, onClose, onSuccess }: { edition: CourseEdition; onClose: () => void; onSuccess?: () => void }) {
  return (
    <InscripcionModal
      isOpen
      onClose={onClose}
      editionId={edition.id}
      editionLabel={edition.edition_label}
      courseName={edition.course?.name || "Curso"}
      coursePrice={edition.price}
      amountPaid={0}
      onSuccess={() => onSuccess?.()}
    />
  );
}

export function InscripcionFromDiplomadoEdition({ edition, onClose, onSuccess }: { edition: DiplomadoEdition; onClose: () => void; onSuccess?: () => void }) {
  return (
    <InscripcionModal
      isOpen
      onClose={onClose}
      editionId={edition.id}
      editionLabel={edition.edition_label}
      courseName={edition.diplomado?.name || "Diplomado"}
      coursePrice={edition.price}
      amountPaid={0}
      serviceType="diplomado"
      onSuccess={() => onSuccess?.()}
    />
  );
}
